import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { StatusIndicator } from './StatusIndicator';
import { ProgressBar } from './ProgressBar';
import { SiteNode } from '../types/types';
import { EnergyColumnVisibility } from './EnergyColumnDefinitions';
import { environmentConfig } from '@/core/api-config';

function formatLastCommunication(isoString: string): string {
  // Ensure UTC parsing — append Z if no timezone designator present
  const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + 'Z';
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '—';
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface TreeNodeRowProps {
  node: SiteNode;
  level: number;
  hasChildren?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  columnWidths?: {
    tree: number;
    sessions: number;
    chargingStatus: number;
    lastCommunication?: number;
    offered: number;
    actual: number;
    allocated: number;
    currentA?: number;
    bufferA?: number;
  };
  columnVisibility: EnergyColumnVisibility;
  rowIndex?: number;
  isTreeViewVisible?: boolean;
  onSessionClick?: (sessionId: string | null, machineName?: string, machineId?: number) => void;
  unitMode?: 'A' | 'kW';
}

export function TreeNodeRow({
  node,
  level,
  hasChildren = false,
  isExpanded = true,
  onToggleExpand,
  columnWidths = { tree: 25, sessions: 6, chargingStatus: 11, lastCommunication: 10, offered: 7, actual: 13, allocated: 28 },
  columnVisibility,
  rowIndex = 0,
  isTreeViewVisible = false,
  onSessionClick,
  unitMode = 'A'
}: TreeNodeRowProps) {
  // Always show hierarchy indentation regardless of tree panel state
  const paddingLeft = level === 0 ? '10px' : `${10 + level * 16}px`;

  // Determine row styling based on hierarchy level
  const isTopLevelParent = level === 0 && hasChildren;
  const isSubGroup = level > 0 && hasChildren;

  // Row styling: parent rows get accent border, leaf rows get zebra striping
  const getRowClasses = () => {
    if (isTopLevelParent) {
      return 'bg-gray-100 border-l-4 border-blue-500';
    }
    if (isSubGroup) {
      return 'bg-gray-50 border-l-4 border-blue-300';
    }
    // Zebra striping for leaf rows
    const zebraClass = rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50';
    return `${zebraClass} border-l-4 border-transparent`;
  };

  const rowClasses = getRowClasses();
  
  // Default widths if not provided - moved before visibleWidths calculation
  const widths = columnWidths || {
    tree: 25,
    sessions: 6,
    chargingStatus: 11,
    lastCommunication: 10,
    offered: 7,
    actual: 13,
    allocated: 28,
  };

  // Filter out hidden columns (width = 0) for responsive display
  const visibleWidths = {
    tree: columnVisibility.tree && widths.tree > 0 ? widths.tree : 0,
    sessions: columnVisibility.sessions && widths.sessions > 0 ? widths.sessions : 0,
    chargingStatus: columnVisibility.chargingStatus && widths.chargingStatus > 0 ? widths.chargingStatus : 0,
    lastCommunication: columnVisibility.lastCommunication && widths.lastCommunication && widths.lastCommunication > 0 ? widths.lastCommunication : 0,
    offered: columnVisibility.offered && widths.offered > 0 ? widths.offered : 0,
    actual: columnVisibility.actual && widths.actual > 0 ? widths.actual : 0,
    allocated: columnVisibility.allocated && widths.allocated > 0 ? widths.allocated : 0,
  };
  
  // Simple function to remove site-X suffixes from names
  const cleanName = (name: string) => {
    const cleaned = name?.replace(/\s*site-\d+$/i, '').trim() || name;
    return cleaned;
  };
  
  const handleActorClick = () => {
    if (node.machineId && !hasChildren) {
      const portalBaseUrl = environmentConfig.getPortalUrl();
      const url = `${portalBaseUrl}/core/public/f?model=operations/machine#${node.machineId}`;
      window.open(url, '_blank');
    }
  };

  const handleLocationClick = () => {
    if (node.id && hasChildren) {
      const portalBaseUrl = environmentConfig.getPortalUrl();
      const url = `${portalBaseUrl}/core/public/f?model=administration/Actors#${node.id}`;
      window.open(url, '_blank');
    }
  };

  // Show button for any node with a machineId that doesn't have children (leaf nodes)
  const isClickable = node.machineId && !hasChildren;
  const isLocationClickable = node.id && hasChildren;
  
  return (
    <div className={`flex hover:bg-blue-50/50 text-sm min-h-[45px] py-2 items-center border-b border-gray-200 ${rowClasses}`}>
      {columnVisibility.tree && visibleWidths.tree > 0 && (
        <div className="flex items-center pl-4" style={{ width: `${visibleWidths.tree}%`, paddingLeft }}>
          {hasChildren && (
            <button
              onClick={onToggleExpand}
              className="mr-2 p-1 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
        
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            {/* Parent nodes (hasChildren) get location icon, leaf nodes get electricity icon */}
            {hasChildren ? (
              <img src="/assets/location.png" alt="" className="w-4 h-4" />
            ) : (
              <img src="/assets/electricity.png" alt="" className="w-4 h-4" />
            )}
          </div>
          
          <div className="flex flex-col gap-1">
            {/* Top row: Machine name (with link for machines) or Location name (with link for actors) */}
            {isClickable ? (
              <button
                onClick={handleActorClick}
                className="text-sm text-blue-600 hover:underline text-left flex items-center gap-1"
                title="Open machine details in portal"
              >
                <span className={node.highlighted ? 'font-medium' : ''}>
                  {cleanName(node.name)}
                </span>
                <ExternalLink className="w-3 h-3" />
              </button>
            ) : isLocationClickable ? (
              <button
                onClick={handleLocationClick}
                className={`text-sm hover:underline text-left flex items-center gap-1 ${node.highlighted ? 'text-blue-600 font-medium' : 'text-gray-900'} ${isTopLevelParent || isSubGroup ? 'font-medium' : ''}`}
                title="Open actor in portal"
              >
                {cleanName(node.name)}
                <ExternalLink className="w-3 h-3 opacity-50" />
              </button>
            ) : (
              <span className={`text-sm ${node.highlighted ? 'text-blue-600 font-medium' : 'text-gray-900'} ${isTopLevelParent || isSubGroup ? 'font-medium' : ''}`}>
                {cleanName(node.name)}
              </span>
            )}
            
            {/* Bottom row: Machine Number | Connector ID (only for machines) */}
            {node.type === 'machine' && (node.machineId || node.evseId) && (
              <span className="text-xs text-gray-500">
                {node.machineId || '-'} | {node.evseId || '-'}
              </span>
            )}
          </div>
        </div>
        </div>
      )}
      
      {columnVisibility.sessions && visibleWidths.sessions > 0 && (
        <div className="flex items-center justify-center border-l border-gray-300 self-stretch" style={{ width: `${visibleWidths.sessions}%` }}>
          {hasChildren ? (
            // For locations: show machinesInSession / totalMachines (always show, even when 0 active sessions)
            node.machinesInSession !== undefined && node.totalMachines !== undefined ? (
              <span className="text-gray-900 text-sm">
                {`${node.machinesInSession}  /  ${node.totalMachines}`}
              </span>
            ) : null
          ) : (
            // For charging stations (machines only): show session ID as clickable link (hide if 0 or empty)
            node.type === 'machine' && node.sessionId && node.sessionId !== '0' ? (
              <button
                onClick={() => onSessionClick?.(node.sessionId, node.name, node.machineId)}
                className="text-sm text-blue-600 hover:underline cursor-pointer"
                title="View session details"
              >
                {node.sessionId}
              </button>
            ) : null
          )}
        </div>
      )}
      
      {columnVisibility.chargingStatus && visibleWidths.chargingStatus > 0 && (
        <div className="flex items-center justify-center border-l border-gray-300 self-stretch" style={{ width: `${visibleWidths.chargingStatus}%` }}>
          {hasChildren ? null : (
            // For charging stations: show status indicator
            node.status ? (
              <StatusIndicator status={node.status} />
            ) : null
          )}
        </div>
      )}
      
      {columnVisibility.lastCommunication && visibleWidths.lastCommunication > 0 && (
        <div className="flex items-center justify-center border-l border-gray-300 self-stretch px-1" style={{ width: `${visibleWidths.lastCommunication}%` }}>
          {node.type === 'machine' && node.lastCommunicationAt ? (
            <span className="text-gray-600 text-xs text-center">
              {formatLastCommunication(node.lastCommunicationAt)}
            </span>
          ) : null}
        </div>
      )}

      {columnVisibility.offered && visibleWidths.offered > 0 && (
        <div className="flex items-center justify-center border-l border-gray-300 self-stretch" style={{ width: `${visibleWidths.offered}%` }}>
          {unitMode === 'kW' ? null : (
            node.offered && parseInt(node.offered) > 0 ? (
              <span className="text-gray-900 text-center">
                {parseInt(node.offered).toString()}
              </span>
            ) : null
          )}
        </div>
      )}
      
      {columnVisibility.actual && visibleWidths.actual > 0 && (
        <div className="flex items-center justify-center border-l border-gray-300 self-stretch" style={{ width: `${visibleWidths.actual}%` }}>
          {(() => {
            // kW mode: show live power for machine, aggregated live power for location
            if (unitMode === 'kW') {
              const kw = hasChildren ? node.actualPowerKw : node.powerKw;
              return kw != null && kw > 0
                ? <span className="text-gray-900">{kw.toFixed(1)}</span>
                : null;
            }
            if (node.actualPhases) {
              const formatPhaseValue = (value: number | string | null | undefined) => {
                if (value === null || value === undefined || value === '') {
                  return '0';
                }
                return typeof value === 'number' ? value.toFixed(1) : parseFloat(String(value)).toFixed(1);
              };

              // Check if all phases are 0 - hide display if so
              const phase1 = Number(formatPhaseValue(node.actualPhases.phase1));
              const phase2 = Number(formatPhaseValue(node.actualPhases.phase2));
              const phase3 = Number(formatPhaseValue(node.actualPhases.phase3));

              if (phase1 === 0 && phase2 === 0 && phase3 === 0) {
                // Fall through to node.actual (e.g. liveCurrentAmps fallback)
                const fallback = node.actual ? parseFloat(node.actual) : 0;
                return fallback > 0 ? (
                  <span className="text-gray-900">{fallback.toFixed(1)}</span>
                ) : null;
              }

              // Single-phase: only phase1 has a value
              if (phase2 === 0 && phase3 === 0) {
                return <span className="text-gray-900">{formatPhaseValue(node.actualPhases.phase1)}</span>;
              }

              const parts = [
                phase1 > 0 ? formatPhaseValue(node.actualPhases.phase1) : null,
                phase2 > 0 ? formatPhaseValue(node.actualPhases.phase2) : null,
                phase3 > 0 ? formatPhaseValue(node.actualPhases.phase3) : null,
              ].filter(Boolean);

              return (
                <span className="text-gray-900">{parts.join(' / ')}</span>
              );
            }

            const val = node.actual ? parseFloat(node.actual) : 0;
            return val > 0 ? (
              <span className="text-gray-900">{val.toFixed(1)}</span>
            ) : null;
          })()}
        </div>
      )}
      
      {columnVisibility.allocated && visibleWidths.allocated > 0 && (
        <div className="flex items-center justify-center border-l border-gray-300 self-stretch px-2" style={{ width: `${visibleWidths.allocated}%` }}>
          {(() => {
            if (hasChildren) {
              // Location row — kW mode: progress bar with actual / limit if data available
              if (unitMode === 'kW') {
                const limit = node.powerKw;
                if (limit == null || limit <= 0) return null;
                const actual = node.actualPowerKw ?? 0;
                const pct = Math.round((actual / limit) * 100 * 10) / 10;
                return (
                  <div className="w-full">
                    <ProgressBar
                      current={parseFloat(actual.toFixed(1))}
                      total={parseFloat(limit.toFixed(1))}
                      percentage={pct}
                      isCharger={false}
                      decimals={1}
                    />
                  </div>
                );
              }
              // Location A mode: progress bar with allocated / limit (buffer) — always show, even when 0
              if (!node.allocated || node.allocated.total <= 0) return null;
              const pct = node.allocated.total > 0
                ? Math.round((node.allocated.current / node.allocated.total) * 100 * 10) / 10
                : 0;
              const bufLabel = node.bufferA != null && node.bufferA > 0 ? ` (${Math.round(node.bufferA)})` : '';
              return (
                <div className="w-full">
                  <ProgressBar
                    current={node.allocated.current}
                    total={node.allocated.total}
                    percentage={pct}
                    isCharger={false}
                  />
                  {bufLabel && <span className="text-xs text-gray-400">{bufLabel}</span>}
                </div>
              );
            } else {
              // Machine row — kW mode
              if (unitMode === 'kW') {
                const pw = node.powerKw != null && node.powerKw > 0 ? node.powerKw : null;
                const maxPw = node.maxPowerKw != null && node.maxPowerKw > 0 ? node.maxPowerKw : null;
                if (pw == null || maxPw == null) return null;
                const pct = Math.round((pw / maxPw) * 100 * 10) / 10;
                return (
                  <ProgressBar
                    current={pw}
                    total={maxPw}
                    percentage={pct}
                    isCharger={true}
                    decimals={1}
                  />
                );
              }
              // Machine A mode
              if (!node.allocated || node.allocated.current <= 0) return null;
              const maxCurr = node.maxCurrentA;
              if (maxCurr == null || maxCurr <= 0) return null;
              const pct = Math.round((node.allocated.current / maxCurr) * 100 * 10) / 10;
              return (
                <ProgressBar
                  current={node.allocated.current}
                  total={maxCurr}
                  percentage={pct}
                  isCharger={true}
                />
              );
            }
          })()}
        </div>
      )}
    </div>
  );
} 